import React, { useState, useEffect } from 'react';
import { trackPageView, trackStepComplete } from './utils/analytics';
import './App.css';
import Header from './components/Header';
import ImageShowcase from './components/ImageShowcase';
import RecipeStory from './components/RecipeStory';
import QuantitySelector from './components/QuantitySelector';
import CalorieDisplay from './components/CalorieDisplay';
import IngredientsSection from './components/IngredientsSection';
import RunbookSection from './components/RunbookSection';
import ProgressSidebar from './components/ProgressSidebar';
import NotificationToast from './components/NotificationToast';
import MobileTimeBanner from './components/MobileTimeBanner';
import Footer from './components/Footer';

function App() {
  // Track page view on component mount
  useEffect(() => {
    trackPageView(window.location.pathname, 'Mumbai Pav Recipe Helper');
  }, []);
  // Recipe data
  const baseRecipe = {
    flour: 1.5, // cups
    milk: 0.5, // cups
    sugar: 0.25, // cups
    egg: 0.5, // count (optional but recommended)
    butter: 2, // tbsp
    yeast: 1.0, // tsp (base unit is 1 teaspoon, not 1 packet)
    salt: 0.5 // tsp
  };

  // Recipe steps data with timers
  const steps = [
    {
      id: 1,
      name: "Mix ingredients A (yeast activation)",
      details: "Mix lukewarm milk + half of sugar then add yeast if you can comfortably put fingers in milk. Put away for 5 minutes, it should froth. If it doesn't froth, throw it away and start again.",
      duration: 5 * 60, // 5 minutes in seconds
      nextAction: "Check if yeast has frothed. If yes, proceed to mix with ingredients B."
    },
    {
      id: 2,
      name: "Mix ingredients B",
      details: "Mix ingredients A with flour and remaining sugar. Knead for 5 minutes.",
      duration: 5 * 60, // 5 minutes in seconds
      nextAction: "Proceed to add ingredients C and continue kneading."
    },
    {
      id: 3,
      name: "Add ingredients C and knead",
      details: "Add egg, salt and knead for 5 more minutes. Use a dough scraper / chef's knife to keep it manageable. If it gets dry, keep the dough moist by making your hands wet with lukewarm water often. Stretch and fold. If at the end of 10min though is still sticky add more flour, not before 10min of kneading. Apply butter only towards the end of 10 min.",
      duration: 5 * 60, // 5 minutes in seconds
      nextAction: "Check dough consistency: it should only stick to itself and not to the container or hands. It should also bounce back when poked with a finger."
    },
    {
      id: 4,
      name: "First rise - first hour",
      details: "Shape the dough into a ball and let it rise for 1 hour, preferably in the oven with the bulb on. The dough should approximately double in size during this time.",
      duration: 60 * 60, // 1 hour in seconds
      nextAction: "Punch down the dough, knead briefly, and prepare for second hour of rising."
    },
    {
      id: 5,
      name: "First rise - second hour",
      details: "After punching down the dough, add flour if it's sticky. Apply some more butter on top and put it back for rising for another hour. Continue to keep it in the oven with the bulb on.",
      duration: 60 * 60, // 1 hour in seconds
      nextAction: "The first rise is complete. Now punch the dough and prepare to divide it."
    },
    {
      id: 6,
      name: "Punch dough and divide",
      details: "Punch the dough and flatten it, divide it into roughly equal parts by cutting it with a knife or dough scraper. Form balls by stretching all sides towards one end.",
      duration: 10 * 60, // 10 minutes in seconds (estimated time to shape)
      nextAction: "Cover the tray with a moist cloth, put away for 1 Hour in the oven (bulb on)."
    },
    {
      id: 7,
      name: "Second rise (1 hour)",
      details: "Cover the tray with a moist cloth, put away for 1 Hour in the oven (bulb on).",
      duration: 60 * 60, // 1 hour in seconds
      nextAction: "After 45 minutes, take the tray away and brush some warm milk on top of the pav."
    },
    {
      id: 8,
      name: "Brush with milk",
      details: "45 minutes into the 1 Hour rising time, take the tray away, brush some warm milk on top of the pav. Put it somewhere else away from moving air (e.g. microwave). Preheat the baking oven to 375F.",
      duration: 15 * 60, // 15 minutes in seconds
      nextAction: "Move the tray into the preheated baking oven (375°F)."
    },
    {
      id: 9,
      name: "Bake (20-25 minutes)",
      details: "Bake at 375°F for 20-25 minutes. Keep checking the color starting 15 minutes into the oven, take out if it's too brown.",
      duration: 20 * 60, // 20 minutes in seconds
      nextAction: "Remove from oven when golden brown, then brush with butter and cover with a moist cloth."
    },
    {
      id: 10,
      name: "Brush with butter and rest",
      details: "Brush butter on the baked pavs and cover with a moist cloth for 10 minutes.",
      duration: 10 * 60, // 10 minutes in seconds
      nextAction: "Your Mumbai Pavs are ready to serve! Enjoy them with your favorite dishes like vada pav, pav bhaji, or misal pav!"
    }
  ];

  // State for quantity and active step
  const [quantity, setQuantity] = useState(6);
  const [activeStepId, setActiveStepId] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [currentStepRemainingTime, setCurrentStepRemainingTime] = useState(0);
  
  // Notification state
  const [notification, setNotification] = useState({
    isOpen: false,
    message: '',
    nextAction: '',
    completedStepId: null
  });
  
  // Function to handle step completion
  const handleStepComplete = (stepId) => {
    const completedStep = steps.find(step => step.id === stepId);
    if (!completedStep) return;
    
    // Track step completion in analytics
    trackStepComplete(stepId, completedStep.name);
    
    // Show notification for the completed step
    setNotification({
      isOpen: true,
      message: `Time's up for ${completedStep.name}!`,
      nextAction: completedStep.nextAction,
      completedStepId: stepId
    });
  };
  
  // Removed unused confirmation dialog state
  
  // Handle notification close
  const handleNotificationClose = () => {
    const stepId = notification.completedStepId;
    
    // Update completed steps
    if (stepId && !completedSteps.includes(stepId)) {
      setCompletedSteps(prev => [...prev, stepId]);
      
      // Start next step if available
      if (stepId < steps.length) {
        setActiveStepId(stepId + 1);
      } else {
        setActiveStepId(null);
      }
    }
    
    // Close the notification
    setNotification(prev => ({
      ...prev,
      isOpen: false
    }));
  };
  
  return (
    <div className="app">
      <div className="container">
        <Header />
        <div className="content-wrapper">
          <aside className="sidebar" id="sidebar">
            <ProgressSidebar activeStepId={activeStepId} steps={steps} />
          </aside>
          <main className="main-content">
            <ImageShowcase />
            <RecipeStory />
            <QuantitySelector quantity={quantity} setQuantity={setQuantity} />
            <CalorieDisplay baseRecipe={baseRecipe} quantity={quantity} />
            <IngredientsSection baseRecipe={baseRecipe} quantity={quantity} />
            <RunbookSection 
              steps={steps} 
              activeStepId={activeStepId} 
              setActiveStepId={setActiveStepId} 
              completedSteps={completedSteps} 
              onStepComplete={handleStepComplete}
              onTimeUpdate={setCurrentStepRemainingTime}
            />
          </main>
        </div>
        <Footer />
        
        {/* Notification Toast */}
        <NotificationToast 
          isOpen={notification.isOpen}
          message={notification.message}
          nextAction={notification.nextAction}
          onClose={handleNotificationClose}
        />
        
        {/* Mobile Time Banner */}
        <MobileTimeBanner 
          activeStepId={activeStepId} 
          steps={steps} 
          remainingTime={currentStepRemainingTime}
        />
      </div>
    </div>
  );
}

export default App;